import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OnboardingTemplate, OnboardingTemplateItem } from '@hcc/types';
import { api } from '../lib/api';

const KEY = ['onboarding-templates'] as const;

/** Write shape: exactly one of role_id / category_id, chosen by id. */
export interface OnboardingTemplateWriteInput {
  role_id?: string | null;
  category_id?: string | null;
  items: readonly OnboardingTemplateItem[];
}

export function useOnboardingTemplates() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (
        await api<{ templates: OnboardingTemplate[] }>(
          '/api/admin/onboarding-templates',
        )
      ).templates,
  });
}

export function useCreateOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardingTemplateWriteInput) =>
      api<{ template: OnboardingTemplate }>('/api/admin/onboarding-templates', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & OnboardingTemplateWriteInput) =>
      api<{ template: OnboardingTemplate }>(
        `/api/admin/onboarding-templates/${input.id}`,
        { method: 'PUT', body: JSON.stringify(input) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/onboarding-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
